import { Controller } from "@hotwired/stimulus";


/**
 * Stimulus controller to handle drag-and-drop sorting of product categories.
 *
 * This controller enables reordering items within the `.admin-products-sortable` list
 * using native Drag and Drop API. The new order is reflected by updating hidden input fields
 * within the `.admin-products-reorder-ids` container, ensuring the correct sequence is submitted
 * with the form.
 *
 * Targets:
 * - `item`: Represents each sortable product category element.
 * - `ids`: The container where hidden inputs for sorted IDs are appended.
 * - `form`: The reorder form element for submission.
 *
 * Events:
 * - `dragstart`: Triggers when a sortable item is picked up.
 * - `dragover`: Handles positioning logic while an item is being dragged.
 * - `dragend`: Finalizes sorting and updates hidden input fields.
 *
 * Example usage in HAML:
 *
 * .content{ 'data-controller': "sortable" }
 *   %ul.admin-products-sortable.list-group.mb-3
 *     %li.admin-product-sortable{ data: { id: 1, sortable_target: "item" }, draggable: "true" } Product 1
 *     %li.admin-product-sortable{ data: { id: 2, sortable_target: "item" }, draggable: "true" } Product 2
 *
 *   .admin-products-reorder-ids{ 'data-sortable-target': "ids" }
 *   = form_tag [:position, :admin, :product_categories], method: :patch, data: { sortable_target: "form" }
 *
 */
export default class extends Controller {
  static targets = ["item", "ids", "form"];

  connect() {
    this.itemTargets.forEach(item => {
      item.setAttribute('draggable', 'true');
      item.addEventListener("dragstart", this.dragStart.bind(this));
      item.addEventListener("dragover", this.dragOver.bind(this));
      item.addEventListener("dragend", this.dragEnd.bind(this));
    });
  }

  dragStart(event) {
    this.draggedItem = event.target;
    this.draggedItem.classList.add("dragging");
  }

  dragOver(event) {
    event.preventDefault();
    const afterElement = this.getDragAfterElement(event.clientY);
    if (afterElement == null) {
      this.element.querySelector('.admin-products-sortable').appendChild(this.draggedItem);
    } else {
      this.element.querySelector('.admin-products-sortable').insertBefore(this.draggedItem, afterElement);
    }
  }

  dragEnd() {
    this.draggedItem.classList.remove("dragging");
    this.updateHiddenInputs();
  }

  getDragAfterElement(y) {
    const draggableElements = this.itemTargets.filter(item => item !== this.draggedItem);
    return draggableElements.reduce((closest, child) => {
      const box = child.getBoundingClientRect();
      const offset = y - box.top - box.height / 2;
      return offset < 0 && offset > closest.offset ? { offset: offset, element: child } : closest;
    }, { offset: Number.NEGATIVE_INFINITY }).element;
  }

  updateHiddenInputs() {
    if (this.hasIdsTarget) {
      this.idsTarget.innerHTML = ""; // Clear previous inputs

      this.itemTargets.forEach(item => {
        const id = item.dataset.id;
        const input = document.createElement("input");
        input.setAttribute("type", "hidden");
        input.setAttribute("name", "ids[]");
        input.setAttribute("value", id);
        this.idsTarget.appendChild(input);
      });
    }
  }
}
